import MailIcon from "@mui/icons-material/MailOutlineRounded";
import { Button, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  emailValidator,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../api/adminApi";
import type { Contact } from "../../../../api/types/fundStructureTypes";

interface Props {
  mode: "add" | "create";
  onClose: () => void;
  onAdd?: (name: string, email: string) => void;
  onCreate?: (newContact: Contact) => void;
  existingContactNames: Set<string>;
  existingContactEmails: Set<string>;
}

const maxNameLength = 100;

interface FormState {
  name: string;
  email: string;
  nameValidationResult: ValidationResult;
  emailValidationResult: ValidationResult;
  nameCollisionWarning?: string;
}

const createContact = withErrorHandling(adminApi.createContact);

const AddContactDialog = ({ mode, onClose, onAdd, onCreate, existingContactNames, existingContactEmails }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);

  const [formState, setFormState] = useState<FormState>({
    name: "",
    email: "",
    nameValidationResult: { isValid: false, error: "" },
    emailValidationResult: { isValid: false, error: "" },
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validateName = combineValidators(requiredValidator, maxCharactersValidator(maxNameLength));

    const nameValidationResult = validateName(e.target.value);

    const nameCollisionWarning =
      nameValidationResult.isValid && existingContactNames.has(e.target.value)
        ? "A contact with this name already exists"
        : undefined;

    setFormState((prev) => ({
      ...prev,
      name: e.target.value,
      nameValidationResult,
      nameCollisionWarning,
    }));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validateEmail = combineValidators(
      requiredValidator,
      emailValidator,
      uniqueValidator("A contact with this email already exists", existingContactEmails)
    );

    const emailValidationResult = validateEmail(sanitizeEmail(e.target.value).toLowerCase());

    setFormState((prev) => ({
      ...prev,
      email: e.target.value,
      emailValidationResult,
    }));
  };

  const handleSubmit = async () => {
    const sanitizedEmail = sanitizeEmail(formState.email);

    if (mode === "add") {
      onClose();
      onAdd?.(formState.name, sanitizedEmail);
      return;
    }

    setIsSaving(true);
    const [resp, error] = await createContact({
      name: formState.name,
      email: sanitizedEmail,
    });
    setIsSaving(false);

    if (error) {
      logError(error, "[AddContactDialog] createContact");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Contact created successfully");
    onClose();
    onCreate?.(resp.contact);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Add Contact</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Full Name"
            variant="outlined"
            value={formState.name}
            onChange={handleNameChange}
            helperText={formState.nameValidationResult.error || formState.nameCollisionWarning}
            error={!!formState.nameValidationResult.error}
            slotProps={{
              htmlInput: { maxLength: maxNameLength },
              input: {
                endAdornment: (
                  <Typography variant="caption" color="textSecondary">
                    {maxNameLength - formState.name.length}
                  </Typography>
                ),
              },
            }}
          />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            value={formState.email}
            onChange={handleEmailChange}
            helperText={formState.emailValidationResult.error}
            error={!!formState.emailValidationResult.error}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MailIcon color="secondary" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          disabled={!formState.nameValidationResult.isValid || !formState.emailValidationResult.isValid}
          onClick={handleSubmit}
          loading={isSaving}
        >
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddContactDialog;
